import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Button, SimpleGrid, Text, useToast, IconButton, Flex, Stack, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Textarea, Checkbox, CheckboxGroup, Spinner
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { supabase } from '../../services/supabaseClient';
import AdminLayout from './AdminLayout';

const PacotesMensalistasAdminPage = () => {
  const [pacotes, setPacotes] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState({ nome: '', valor_mensal: '', descricao: '', servicos: [] });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Carrega pacotes e serviços
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data: pacotesData } = await supabase.from('pacotes_mensalistas').select('*').order('nome');
      const { data: servicosData } = await supabase.from('servicos').select('*').order('nome');
      setPacotes(pacotesData || []);
      setServicos(servicosData || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Abre modal para criar ou editar
  const handleOpenModal = async (pacote = null) => {
    if (pacote) {
      // Buscar serviços já cadastrados nesse pacote
      const { data: servicosPacote } = await supabase
        .from('pacote_servicos')
        .select('servico_id, quantidade_inclusa')
        .eq('pacote_id', pacote.id);
      setForm({
        nome: pacote.nome,
        valor_mensal: pacote.valor_mensal,
        descricao: pacote.descricao || '',
        servicos: servicosPacote ? servicosPacote.map(s => ({ servico_id: s.servico_id, quantidade: s.quantidade_inclusa })) : []
      });
      setEditId(pacote.id);
    } else {
      setForm({ nome: '', valor_mensal: '', descricao: '', servicos: [] });
      setEditId(null);
    }
    onOpen();
  };


  // Salvar pacote (criar ou editar)
  const handleSave = async () => {
    setSaving(true);
    if (!form.nome || !form.valor_mensal) {
      toast({ title: 'Nome e valor são obrigatórios', status: 'warning' });
      setSaving(false);
      return;
    }
    let pacoteId = editId;
    let result;
    if (editId) {
      // Atualiza pacote
      result = await supabase.from('pacotes_mensalistas').update({
        nome: form.nome,
        valor_mensal: form.valor_mensal,
        descricao: form.descricao
      }).eq('id', editId);
      // Remove todos os serviços antigos do pacote
      await supabase.from('pacote_servicos').delete().eq('pacote_id', editId);
    } else {
      // Cria novo pacote
      result = await supabase.from('pacotes_mensalistas').insert({
        nome: form.nome,
        valor_mensal: form.valor_mensal,
        descricao: form.descricao
      }).select();
      if (result.data && result.data[0]) {
        pacoteId = result.data[0].id;
      }
    }
    // Agora insere os serviços do pacote
    if (!result.error && pacoteId && form.servicos && form.servicos.length > 0) {
      const servicosToInsert = form.servicos.map(s => ({
        pacote_id: pacoteId,
        servico_id: s.servico_id,
        quantidade_inclusa: Number(s.quantidade) || 1
      }));
      const insertResult = await supabase.from('pacote_servicos').insert(servicosToInsert);
      if (insertResult.error) {
        toast({ title: 'Erro ao salvar serviços do pacote', description: insertResult.error.message, status: 'error' });
        setSaving(false);
        return;
      }
    }
    if (!result.error) {
      toast({ title: 'Pacote salvo!', status: 'success' });
      onClose();
      setForm({ nome: '', valor_mensal: '', descricao: '', servicos: [] });
      setEditId(null);
      // Recarrega
      const { data: pacotesData } = await supabase.from('pacotes_mensalistas').select('*').order('nome');
      setPacotes(pacotesData || []);
    } else {
      toast({ title: 'Erro ao salvar', description: result.error.message, status: 'error' });
    }
    setSaving(false);
  };


  // Excluir pacote
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este pacote?')) return;
    const { error } = await supabase.from('pacotes_mensalistas').delete().eq('id', id);
    if (!error) {
      toast({ title: 'Pacote excluído!', status: 'success' });
      setPacotes(pacotes.filter(p => p.id !== id));
    } else {
      toast({ title: 'Erro ao excluir', description: error.message, status: 'error' });
    }
  };

  return (
    <AdminLayout>
      <Box w="100%" maxW="900px" mx="auto" px={{ base: 2, md: 8 }} py={8}>
        <Flex justify="space-between" align="center" mb={6} direction={{ base: 'column', sm: 'row' }} gap={4}>
          <Heading fontSize={{ base: '2xl', md: '3xl' }}>Pacotes de Mensalistas</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="yellow" onClick={() => handleOpenModal()}>Novo Pacote</Button>
        </Flex>
        {loading ? (
          <Spinner size="lg" mt={10} />
        ) : (
          <Box overflowX="auto">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {pacotes.map((pacote) => (
                <Box key={pacote.id} bg="gray.800" borderRadius="xl" p={5} boxShadow="md" display="flex" flexDir="column" gap={2}>
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="bold" fontSize="xl" color="yellow.300">{pacote.nome}</Text>
                    <Flex gap={2}>
                      <IconButton aria-label="Editar" icon={<EditIcon />} size="sm" colorScheme="yellow" variant="ghost" onClick={() => handleOpenModal(pacote)} />
                      <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="sm" colorScheme="red" variant="ghost" onClick={() => handleDelete(pacote.id)} />
                    </Flex>
                  </Flex>
                  <Text color="whiteAlpha.800">Valor mensal: <b>R$ {Number(pacote.valor_mensal).toFixed(2)}</b></Text>
                  <Text color="whiteAlpha.700" fontSize="sm">{pacote.descricao}</Text>
                  {/* Exibir serviços inclusos depois */}
                </Box>
              ))}
            </SimpleGrid>
            {pacotes.length === 0 && (
              <Text color="whiteAlpha.700" textAlign="center" mt={8}>Nenhum pacote cadastrado ainda.</Text>
            )}
          </Box>
        )}
      </Box>

      {/* Modal de criar/editar pacote */}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "md", md: "lg" }} isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
        <ModalContent 
          bg="gray.800" 
          color="white" 
          borderRadius="xl" 
          boxShadow="xl"
          mx={{ base: 3, md: 0 }}
          my={{ base: 3, md: 0 }}
          maxH={{ base: "calc(100vh - 80px)", md: "auto" }}
          overflowY="auto"
        >
          <ModalHeader borderBottomWidth="1px" borderColor="gray.700" py={4} px={6}>
            <Flex align="center">
              {editId ? (
                <EditIcon color="yellow.300" mr={3} />
              ) : (
                <AddIcon color="yellow.300" mr={3} />
              )}
              <Text fontSize="xl" fontWeight="bold" color="white">
                {editId ? 'Editar Pacote' : 'Novo Pacote'}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6} px={{ base: 4, md: 6 }}>
            <Stack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Nome do Pacote</FormLabel>
                <Input 
                  value={form.nome} 
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} 
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  placeholder="Nome do pacote"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Valor Mensal</FormLabel>
                <Input
                  type="number"
                  min={0}
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  value={form.valor_mensal}
                  onChange={e => {
                    const val = e.target.value;
                    setForm(f => ({ ...f, valor_mensal: val }));
                  }}
                  placeholder="0,00"
                />
                {form.valor_mensal && !isNaN(Number(form.valor_mensal)) && (
                  <Text mt={1} fontSize="sm" color="yellow.300">
                    {Number(form.valor_mensal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Text>
                )}
              </FormControl>
              
              <FormControl>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Descrição</FormLabel>
                <Textarea 
                  value={form.descricao} 
                  onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} 
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  placeholder="Descreva o pacote..."
                  rows={3}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Serviços inclusos</FormLabel>
                <Stack 
                  spacing={2} 
                  maxH="220px" 
                  overflowY="auto" 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="gray.600" 
                  p={3} 
                  bg="gray.700"
                >
                  {servicos.map((servico) => {
                    const checked = form.servicos.some(s => s.servico_id === servico.id);
                    const quantidade = form.servicos.find(s => s.servico_id === servico.id)?.quantidade || 1;
                    return (
                      <Flex key={servico.id} align="center" gap={2}>
                        <Checkbox
                          isChecked={checked}
                          onChange={e => {
                            setForm(f => {
                              if (e.target.checked) {
                                return { ...f, servicos: [...f.servicos, { servico_id: servico.id, quantidade: 1 }] };
                              } else {
                                return { ...f, servicos: f.servicos.filter(s => s.servico_id !== servico.id) };
                              }
                            });
                          }}
                          colorScheme="yellow"
                          size="md"
                        >
                          <Text color="white">{servico.nome}</Text>
                        </Checkbox>
                        <NumberInput
                          min={1}
                          max={20}
                          value={quantidade === undefined ? '' : quantidade}
                          isDisabled={!checked}
                          onChange={(valorStr) => {
                            setForm(f => ({
                              ...f,
                              servicos: f.servicos.map(s => s.servico_id === servico.id ? { ...s, quantidade: valorStr } : s)
                            }));
                          }}
                          size="sm"
                          w="70px"
                          bg="gray.600"
                          borderRadius="md"
                        >
                          <NumberInputField 
                            inputMode="none" 
                            readOnly 
                            color="white" 
                            borderColor="gray.500"
                            _hover={{ borderColor: "yellow.300" }}
                          />
                          <NumberInputStepper borderColor="gray.500">
                            <NumberIncrementStepper color="yellow.300" borderColor="gray.500" />
                            <NumberDecrementStepper color="yellow.300" borderColor="gray.500" />
                          </NumberInputStepper>
                        </NumberInput>
                      </Flex>
                    );
                  })}
                  {servicos.length === 0 && <Text color="whiteAlpha.600">Nenhum serviço cadastrado</Text>}
                </Stack>
              </FormControl>
            </Stack>
          </ModalBody>
          
          <ModalFooter borderTopWidth="1px" borderColor="gray.700" py={4} px={6}>
            <Flex justify="space-between" w="100%" direction={{ base: 'column', sm: 'row' }} gap={3}>
              <Button 
                variant="outline" 
                onClick={onClose} 
                color="white" 
                borderColor="gray.600"
                _hover={{ bg: "gray.700" }}
                width={{ base: "100%", sm: "auto" }}
              >
                Cancelar
              </Button>
              <Button 
                colorScheme="yellow" 
                onClick={handleSave} 
                isLoading={saving}
                leftIcon={editId ? <EditIcon /> : <AddIcon />}
                width={{ base: "100%", sm: "auto" }}
                _hover={{ bg: "yellow.500" }}
              >
                {editId ? 'Atualizar Pacote' : 'Salvar Pacote'}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
};

export default PacotesMensalistasAdminPage;
